/* The analytics navbar buttons */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import classNames from 'classnames';
import { analyticsUpgradeUrl } from 'js/analytics/upgradeUrl';
import {
  NewAnalyticsBasePathContext,
  newAnalyticsDestination,
} from 'js/analytics/AnalyticsExperienceBoundary';

import PremiumIcon from 'js/premium/PremiumIcon';
import PremiumUpsellPopover from 'js/premium/PremiumUpsellPopover';
import {
  analyticsImpressionId,
  currentAnalyticsTab,
  trackAnalyticsClick,
  watchAnalyticsImpression,
} from 'js/analytics/metrics/analyticsTracking';

export const HrefOrLink = ({ href, exact, strict, isActive, ...props }) => {
  if (href) {
    return <a {...props} href={href} className="subnav-link" />;
  }
  return (
    <NavLink
      {...{ ...props, exact, strict, isActive }}
      className="subnav-link"
    />
  );
};

export class ActivityLink extends Component {
  static contextType = NewAnalyticsBasePathContext;

  static propTypes = {
    className: PropTypes.string,
    onActive: PropTypes.func,
    premiumFeature: PropTypes.bool,
    isPremium: PropTypes.bool,
    isExternal: PropTypes.bool,
    tabKey: PropTypes.string,
    upgradeTrigger: PropTypes.string,
  };

  state = {
    premiumUpsellOpen: false,
  };

  componentDidMount() {
    this.syncLockedTabImpression();
  }

  componentDidUpdate() {
    this.syncLockedTabImpression();
  }

  componentWillUnmount() {
    this.impressionWatcher?.stop();
  }

  render() {
    const { props, state } = this;
    const { premiumFeature, isPremium } = this.props;
    const newAnalyticsHref = this.newAnalyticsHref();
    const locked = Boolean(premiumFeature) && !isPremium && !newAnalyticsHref;
    const trigger = props.upgradeTrigger || null;

    if (props.hide) return null;

    const upgradeUrl = !locked
      ? null
      : analyticsUpgradeUrl({
          feature: premiumFeature,
          trigger: props.upgradeTrigger,
        });

    return (
      <div
        ref={this.setImpressionElement}
        className={props.className}
        onMouseEnter={this.handleMouseEnter}
        onMouseLeave={this.handleMouseLeave}
      >
        <HrefOrLink
          to={!props.isExternal ? props.to : undefined}
          // eslint-disable-next-line prettier/prettier
          href={
            newAnalyticsHref ||
            (props.isExternal ? props.to : props.href || upgradeUrl)
          }
          exact={props.exact}
          isActive={this.isActive}
          onClick={this.handleTabClick}
        >
          <span style={{ display: 'flex' }}>
            {Boolean(premiumFeature) && (
              <PremiumIcon marginRight={6} size={12} />
            )}

            {Boolean(props.text) && (
              <span
                className="analytics-dashboard-subnav-text"
                text={props.text}
              >
                {props.text}
              </span>
            )}

            {Boolean(props.children) && <span>{props.children}</span>}

            {Boolean(props.redDot) && (
              <span className="NavUnreadBubble NavUnreadBubble--afterText" />
            )}
          </span>
        </HrefOrLink>
        {locked && (
          <PremiumUpsellPopover
            show={state.premiumUpsellOpen}
            bodyText={props.popoverBodyText}
            buttonUrl={upgradeUrl}
            buttonText={props.popoverButtonText}
            onCtaClick={() =>
              trackAnalyticsClick('analytics-upsell-popover', {
                to_tab: props.tabKey,
                trigger,
              })
            }
            impression={{
              source: 'analytics-upsell-popover',
              entityId: analyticsImpressionId(trigger || ''),
              extra: {
                to_tab: props.tabKey,
                trigger,
                tab: currentAnalyticsTab(),
                is_premium: Boolean(isPremium),
              },
            }}
          />
        )}
      </div>
    );
  }

  setImpressionElement = (element) => {
    if (element === this.impressionElement) {
      return;
    }
    this.impressionWatcher?.stop();
    this.impressionWatcher = null;
    this.impressionSignature = undefined;
    this.impressionElement = element;
    this.syncLockedTabImpression();
  };

  syncLockedTabImpression = () => {
    const { premiumFeature, isPremium, tabKey, upgradeTrigger } = this.props;
    const locked = Boolean(premiumFeature) && !isPremium;
    const signature =
      locked && tabKey ? `${tabKey}:${upgradeTrigger || ''}` : null;

    if (signature === this.impressionSignature) return;

    this.impressionWatcher?.stop();
    this.impressionWatcher = null;
    this.impressionSignature = signature;

    if (signature && this.impressionElement) {
      this.impressionWatcher = watchAnalyticsImpression(
        this.impressionElement,
        'analytics-tab',
        tabKey,
        {
          to_tab: tabKey,
          trigger: upgradeTrigger || null,
        },
      );
    }
  };

  handleTabClick = () => {
    const { premiumFeature, isPremium, tabKey, upgradeTrigger } = this.props;
    const locked =
      Boolean(premiumFeature) && !isPremium && !this.newAnalyticsHref();
    const trigger = upgradeTrigger || null;

    trackAnalyticsClick('analytics-tab', {
      to_tab: tabKey,
      from_tab: currentAnalyticsTab(),
      locked,
      trigger,
    });
  };

  newAnalyticsHref = () =>
    newAnalyticsDestination(
      this.context,
      this.props.to,
      window.location.search,
    );

  handleMouseEnter = () => {
    clearTimeout(this.hideTimeout);
    this.setState({ premiumUpsellOpen: true });
  };

  handleMouseLeave = () => {
    this.hideTimeout = setTimeout(() => {
      this.setState({ premiumUpsellOpen: false });
    }, 200);
  };
}

export const ActivityListLink = ({ className, ...props }) => (
  <li className="u-floatLeft">
    <ActivityLink
      {...props}
      className={classNames(
        className,
        'analytics-dashboard-subnav-link u-fs12 u-positionRelative',
        ' u-clickable PremiumIconContainerHoverWrapper',
      )}
    />
  </li>
);
